import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { UserService, SpotifyUser } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth.service';
import { PlaylistSettingsDialogComponent } from '../playlist-settings-dialog/playlist-settings-dialog.component';

@Component({
  selector: 'app-profile-mini-card',
  template: `
    <div class="profile-container" *ngIf="user">
      <div class="profile-info">
        <div class="profile-avatar">
          <img 
            *ngIf="user.images && user.images.length > 0; else defaultAvatar"
            [src]="getProfileImageUrl()" 
            [alt]="user.display_name"
            class="avatar-image"
            (error)="onImageError($event)"
          >
          <ng-template #defaultAvatar>
            <mat-icon class="default-avatar">account_circle</mat-icon>
          </ng-template>
        </div>
        <div class="profile-details">
          <div class="display-name">{{ user.display_name || user.id }}</div>
          <div class="user-info">
            <span class="premium-badge" *ngIf="user.product === 'premium'">
              <mat-icon class="premium-icon">star</mat-icon>
              Premium
            </span>
            <span class="free-badge" *ngIf="user.product === 'free'">Free</span>
            <span class="followers" *ngIf="user.followers">
              {{ user.followers.total | number }} followers
            </span>
          </div>
        </div>
      </div>
      <button 
        mat-icon-button 
        [matMenuTriggerFor]="profileMenu"
        class="menu-button"
        aria-label="Profile menu"
      >
        <mat-icon>more_vert</mat-icon>
      </button>
    </div>

    <mat-menu #profileMenu="matMenu" class="profile-menu">
      <button mat-menu-item (click)="viewProfile()">
        <mat-icon>person</mat-icon>
        <span>View Profile</span>
      </button>
      <button mat-menu-item (click)="openSettings()">
        <mat-icon>settings</mat-icon>
        <span>Settings</span>
      </button>
      <mat-divider></mat-divider>
      <button mat-menu-item (click)="logout()" class="logout-button">
        <mat-icon>logout</mat-icon>
        <span>Logout</span>
      </button>
    </mat-menu>

    <!-- Loading state -->
    <div class="profile-loading" *ngIf="!user && !error">
      <mat-spinner diameter="32"></mat-spinner>
    </div>

    <!-- Error state -->
    <div class="profile-error" *ngIf="error">
      <mat-icon class="error-icon">error</mat-icon>
      <span class="error-text">Failed to load profile</span>
    </div>
  `,
  styles: [`
    .profile-container {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      min-width: 200px;
      max-width: 280px;
    }

    .profile-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      overflow: hidden;
    }

    .profile-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .avatar-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }

    .default-avatar {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: rgba(255, 255, 255, 0.7);
    }

    .profile-details {
      flex: 1;
      overflow: hidden;
      color: white;
    }

    .display-name {
      font-weight: 500;
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 2px;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      opacity: 0.8;
      flex-wrap: wrap;
    }

    .premium-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      background: linear-gradient(45deg, #1db954, #1ed760);
      padding: 2px 6px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 500;
      color: white;
    }

    .premium-icon {
      font-size: 12px;
      width: 12px;
      height: 12px;
    }

    .free-badge {
      background: rgba(255, 255, 255, 0.2);
      padding: 2px 6px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 500;
    }

    .followers {
      font-size: 11px;
      opacity: 0.7;
    }

    .menu-button {
      color: white;
      opacity: 0.8;
    }

    .menu-button:hover {
      opacity: 1;
    }

    .profile-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
    }

    .profile-error {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      color: #f44336;
      font-size: 12px;
    }

    .error-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .logout-button {
      color: #f44336;
    }

    .profile-menu {
      margin-top: 8px;
    }

    @media (max-width: 768px) {
      .profile-container {
        min-width: auto;
        max-width: 180px;
      }
      
      .display-name {
        font-size: 13px;
      }
      
      .user-info {
        font-size: 11px;
      }
      
      .followers {
        display: none;
      }
    }
  `]
})
export class ProfileMiniCardComponent implements OnInit, OnDestroy {
  user: SpotifyUser | null = null;
  error: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    // Subscribe to user changes
    this.userService.user$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.user = user;
    });

    // Load user profile if not already loaded
    if (!this.userService.getCurrentUser()) {
      this.loadUserProfile();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserProfile() {
    this.userService.getUserProfile().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (user) => {
        this.user = user;
        this.error = false;
      },
      error: (error) => {
        console.error('Failed to load user profile:', error);
        this.error = true;
      }
    });
  }

  getProfileImageUrl(): string {
    if (this.user?.images && this.user.images.length > 0) {
      // Return the smallest image for better performance
      return this.user.images[this.user.images.length - 1].url;
    }
    return '';
  }

  onImageError(event: any) {
    // Hide broken image and show default avatar
    event.target.style.display = 'none';
  }

  viewProfile() {
    // Open Spotify profile in new tab
    if (this.user?.id) {
      window.open(`https://open.spotify.com/user/${this.user.id}`, '_blank');
    }
  }

  openSettings() {
    const dialogRef = this.dialog.open(PlaylistSettingsDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: { initialTab: 0 } // Start with Basic tab
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.saved) {
        console.log('Settings saved:', result.settings);
      }
    });
  }

  logout() {
    this.authService.logout().subscribe({
      next: () => {
        this.userService.clearUserData();
        this.router.navigate(['/auth/login'], { replaceUrl: true });
      },
      error: (error) => {
        console.error('Logout error:', error);
        // Still clear local data and redirect even if backend call fails
        this.userService.clearUserData();
        this.router.navigate(['/auth/login'], { replaceUrl: true });
      }
    });
  }
}